import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, ExternalLink } from 'lucide-react';
import { API_BASE_URL } from '../apiConfig';
import mapImg from './../images/map.png';
import mailImg from './../images/contact-mail.png';
import phoneImg from './../images/contact-phone.png';
import locationImg from './../images/contact-location.png';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phoneNo: '',
        yourMsg: ''
    });
    const [formErrors, setFormErrors] = useState({ 
        name: '', 
        email: '', 
        phoneNo: '', 
        yourMsg: '' 
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const isMobile = windowWidth <= 768;

    const validateField = (name, value) => {
        let error = '';
        if (name === 'email' && !value.match(/^([\w.%+-]+)@([\w-]+\.)+([\w]{2,})$/i)) {
            error = 'Invalid email address';
        } else if (value.length < 1) {
            error = 'This field is required';
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        setFormErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
    };

    const isFormValid = () => {
        return (
            formData.name && 
            formData.email && 
            formData.phoneNo && 
            formData.yourMsg && 
            !Object.values(formErrors).some(err => err)
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus('Sending...');

        try {
            const payload = {
                name: formData.name,
                admin_email: formData.email,
                mobile: formData.phoneNo,
                msg: formData.yourMsg
            };
            const res = await axios.post(`${API_BASE_URL}/mail`, payload);
            setSubmitStatus(res.data || 'Message sent successfully!');
            setFormData({ name: '', email: '', phoneNo: '', yourMsg: '' });
        } catch (error) {
            setSubmitStatus('Failed to send message. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const styles = {
        wrapper: {
            width: '100%',
            background: '#f8f9fa',
            minHeight: '100vh',
            paddingTop: isMobile ? '100px' : '112px',
        },
        hero: {
            width: '100%',
            height: isMobile ? '200px' : '300px',
            background: 'radial-gradient(circle at center, #0B15C5 0%, #060D8A 100%)',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            padding: '0 20px',
            position: 'relative',
            overflow: 'hidden',
        },
        heroTitle: {
            fontFamily: "'Anton', sans-serif",
            fontSize: isMobile ? '40px' : '64px',
            color: '#FFFFFF',
            textTransform: 'uppercase',
            margin: 0,
            letterSpacing: '2px',
            zIndex: 1,
        },
        heroSubtitle: {
            fontFamily: 'var(--fontInter)',
            fontSize: isMobile ? '14px' : '18px',
            color: 'rgba(255,255,255,0.7)',
            margin: '8px 0 0 0',
            maxWidth: '600px',
            zIndex: 1,
        },
        contentSection: {
            maxWidth: '1200px',
            margin: '48px auto 80px',
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            background: '#FFFFFF',
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            overflow: 'hidden',
            zIndex: 10,
            position: 'relative',
        },
        infoPanel: {
            background: '#060D8A',
            color: '#FFFFFF',
            padding: '48px',
            flex: '1',
            display: 'flex',
            flexDirection: 'column',
            gap: '40px',
        },
        contactItem: {
            display: 'flex',
            gap: '20px',
            alignItems: 'flex-start',
        },
        iconBox: {
            width: '48px',
            height: '48px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
        },
        itemTitle: {
            fontFamily: "'Anton', sans-serif",
            fontSize: '18px',
            margin: '0 0 4px 0',
            textTransform: 'uppercase',
            letterSpacing: '1px',
        },
        itemText: {
            fontFamily: 'var(--fontInter)',
            fontSize: '15px',
            color: 'rgba(255,255,255,0.7)',
            margin: 0,
            lineHeight: '1.5',
        },
        formPanel: {
            padding: isMobile ? '32px 24px' : '48px',
            flex: '1.2',
            background: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
        },
        formGrid: {
            display: 'flex',
            flexDirection: 'column',
            gap: '32px',
        },
        inputGroup: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
        },
        label: {
            fontFamily: 'var(--fontInter)',
            fontSize: '13px',
            fontWeight: 700,
            color: '#333333',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
        },
        input: {
            width: '100%',
            padding: '12px 16px',
            background: '#f8f9fa',
            border: '2px solid transparent',
            borderRadius: '12px',
            fontFamily: 'var(--fontInter)',
            fontSize: '15px',
            outline: 'none',
            transition: 'all 0.3s ease',
            boxSizing: 'border-box',
        },
        textarea: {
            width: '100%',
            padding: '12px 16px',
            background: '#f8f9fa',
            border: '2px solid transparent',
            borderRadius: '12px',
            fontFamily: 'var(--fontInter)',
            fontSize: '15px',
            outline: 'none',
            minHeight: '150px',
            resize: 'vertical',
            transition: 'all 0.3s ease',
            boxSizing: 'border-box',
        },
        submitBtn: {
            width: '100%',
            padding: '16px',
            background: '#E7C424',
            color: '#060D8A',
            border: 'none',
            borderRadius: '12px',
            fontFamily: "'Anton', sans-serif",
            fontSize: '18px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            transition: 'all 0.3s ease',
        },
        mapContainer: {
            marginTop: '32px',
            borderRadius: '16px',
            overflow: 'hidden',
            border: '3px solid #E7C424',
            position: 'relative',
        },
        mapImg: {
            width: '100%',
            height: '240px',
            objectFit: 'cover',
            display: 'block',
        }
    };

    const inputFocusStyle = (e) => {
        e.target.style.background = '#FFFFFF';
        e.target.style.borderColor = '#060D8A';
        e.target.style.boxShadow = '0 0 0 4px rgba(6,13,138,0.1)';
    };

    const inputBlurStyle = (e) => {
        e.target.style.background = '#f8f9fa';
        e.target.style.borderColor = 'transparent';
        e.target.style.boxShadow = 'none';
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.hero}>
                <motion.h1 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={styles.heroTitle}
                >
                    Get In Touch
                </motion.h1>
                <motion.p 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    style={styles.heroSubtitle}
                >
                    Have questions? Join the greatest cricket league and hit some big sixes! Our team is here to help.
                </motion.p>
            </div>

            <motion.div 
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                style={styles.contentSection}
            >
                <div style={styles.infoPanel}>
                    <div style={styles.contactItem}>
                        <div style={styles.iconBox}><Mail color="#E7C424" size={24} /></div>
                        <div>
                            <h3 style={styles.itemTitle}>Chat with us</h3>
                            <p style={styles.itemText}>Our team will ping you within hours</p>
                            <p style={{ ...styles.itemText, color: '#E7C424', fontWeight: 700 }}>hello@btcluk.com</p>
                        </div>
                    </div>

                    <div style={styles.contactItem}>
                        <div style={styles.iconBox}><Phone color="#E7C424" size={24} /></div>
                        <div>
                            <h3 style={styles.itemTitle}>Call Us</h3>
                            <p style={styles.itemText}>Mon - Fri from 9AM to 3PM</p>
                            <p style={{ ...styles.itemText, color: '#E7C424', fontWeight: 700 }}>020 8150 5667</p>
                        </div>
                    </div>

                    <div style={styles.contactItem}>
                        <div style={styles.iconBox}><MapPin color="#E7C424" size={24} /></div>
                        <div>
                            <h3 style={styles.itemTitle}>Visit HQ</h3>
                            <p style={styles.itemText}>British Tamils Cricket League (BTCL)</p>
                            <p style={styles.itemText}>14 Elm Road, Chessington</p>
                            <p style={styles.itemText}>Surrey KT9 1AW</p>
                        </div>
                    </div>

                    <div style={styles.mapContainer}>
                        <a target="_blank" rel="noreferrer" href="https://www.google.com/maps/place/British+Tamils+Cricket+League+(BTCL)/@51.36765,-0.3059016,17z">
                            <img src={mapImg} alt="Map" style={styles.mapImg} />
                            <div style={{ position: 'absolute', bottom: '12px', right: '12px', display: 'flex', gap: '8px', alignItems: 'center', background: '#E7C424', color: '#060D8A', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 700 }}>
                                View on Maps <ExternalLink size={14} />
                            </div>
                        </a>
                    </div>
                </div>

                <div style={styles.formPanel}>
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', flex: 1 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
                            <div style={styles.formGrid}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Full Name</label>
                                    <input 
                                        name="name"
                                        placeholder="John Doe"
                                        style={styles.input} 
                                        onFocus={inputFocusStyle}
                                        onBlur={inputBlurStyle}
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                    {formErrors.name && <span style={{ color: '#CC2229', fontSize: '11px' }}>{formErrors.name}</span>}
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Phone Number</label>
                                    <input 
                                        name="phoneNo"
                                        placeholder="+44 1234 567890"
                                        style={styles.input} 
                                        onFocus={inputFocusStyle}
                                        onBlur={inputBlurStyle}
                                        value={formData.phoneNo}
                                        onChange={handleChange}
                                    />
                                    {formErrors.phoneNo && <span style={{ color: '#CC2229', fontSize: '11px' }}>{formErrors.phoneNo}</span>}
                                </div>
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Email Address</label>
                                <input 
                                    name="email"
                                    type="email"
                                    placeholder="john@example.com"
                                    style={styles.input} 
                                    onFocus={inputFocusStyle}
                                    onBlur={inputBlurStyle}
                                    value={formData.email}
                                    onChange={handleChange}
                                />
                                {formErrors.email && <span style={{ color: '#CC2229', fontSize: '11px' }}>{formErrors.email}</span>}
                            </div>

                            <div style={styles.inputGroup}>
                                <label style={styles.label}>Your Message</label>
                                <textarea 
                                    name="yourMsg"
                                    placeholder="How can we help you?"
                                    style={styles.textarea} 
                                    onFocus={inputFocusStyle}
                                    onBlur={inputBlurStyle}
                                    value={formData.yourMsg}
                                    onChange={handleChange}
                                />
                                {formErrors.yourMsg && <span style={{ color: '#CC2229', fontSize: '11px' }}>{formErrors.yourMsg}</span>}
                            </div>
                        </div>

                        <div style={{ textAlign: 'center', marginTop: '32px' }}>
                            {submitStatus && <p style={{ color: submitStatus.includes('Failed') ? '#CC2229' : 'green', fontSize: '14px', marginBottom: '16px', fontWeight: 700 }}>{submitStatus}</p>}
                            <motion.button 
                                whileHover={isFormValid() ? { scale: 1.02, backgroundColor: '#f1d03c' } : {}}
                                whileTap={isFormValid() ? { scale: 0.98 } : {}}
                                style={{ ...styles.submitBtn, opacity: isFormValid() ? 1 : 0.6, cursor: isFormValid() ? 'pointer' : 'not-allowed' }}
                                disabled={!isFormValid() || isSubmitting}
                                type="submit"
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'} <Send size={20} />
                            </motion.button>
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};

export default Contact;